import { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { styles as baseStyles, Checkbox, FormInput, RadioButtonGroup, Text, theme } from '@socrait/react';

import { HyperLink } from '~/components/HyperLink';
import { Select } from '~/components/Select';
import { handleTermsOfServicePress } from '~/constants';
import { setupStore, LOCATION_OPTIONS } from '~/store/setupStore';
import { useUserProfile } from '~/services/useUserProfileQuery';

import { setupStepStyles } from './setupStepStyles';

const LOCATION_SELECT_OPTIONS = LOCATION_OPTIONS.map((state) => ({ label: state, value: state }));

export const TeacherSetupStep = () => {
  const {
    referredToName,
    setReferredToName,
    schoolName,
    setSchoolName,
    location,
    setLocation,
    teachingStyle,
    setTeachingStyle,
    tosAgreedAt,
    setTosAgreedAt,
  } = setupStore();
  const { user } = useUserProfile();

  // Seed the store with the existing ToS agreement from the user profile so that
  // validation and the final submission payload work without any extra changes.
  useEffect(() => {
    if (user?.tosAgreedAt && !tosAgreedAt) {
      setTosAgreedAt(user.tosAgreedAt);
    }
  }, [user?.tosAgreedAt, tosAgreedAt, setTosAgreedAt]);

  const handleAgreedToTermsToggle = useCallback(() => {
    if (!tosAgreedAt) {
      setTosAgreedAt(new Date().toISOString());
    } else {
      setTosAgreedAt('');
    }
  }, [tosAgreedAt, setTosAgreedAt]);

  const checkboxLabel = useMemo(
    () => (
      <View style={styles.checkboxLabelContainer}>
        <Text style={styles.checkboxLabelText}>I agree to the </Text>
        <HyperLink onPress={handleTermsOfServicePress} style={styles.checkboxLabelText}>
          Terms of Service<Text style={[styles.checkboxLabelText, { color: theme.colors.red }]}>*</Text>
        </HyperLink>
      </View>
    ),
    [],
  );

  const teachingStyleOptions = useMemo(
    () => [
      { label: 'the same group of students all day', value: 'single', telemetryLabel: 'teaching-style-single-radio' },
      {
        label: 'different groups of students throughout the day',
        value: 'multiple',
        telemetryLabel: 'teaching-style-multiple-radio',
      },
    ],
    [],
  );

  return (
    <View style={setupStepStyles.container}>
      <View style={setupStepStyles.headerContainer}>
        <View style={setupStepStyles.headerContainer}>
          <Text style={setupStepStyles.headerText}>
            Hello, <Text style={setupStepStyles.headerTextPurple}>{user?.name}</Text>
          </Text>
        </View>
      </View>

      <View style={setupStepStyles.contentContainer}>
        <FormInput
          autoCorrect={false}
          blurOnSubmit={true}
          label="What do students call you?"
          onChangeText={setReferredToName}
          placeholderText="Enter your name"
          required={true}
          telemetryLabel="teacher-setup-referred-to-name-input"
          value={referredToName}
          variant="mobile"
        />

        <FormInput
          autoCorrect={false}
          blurOnSubmit={true}
          label="Your school"
          onChangeText={setSchoolName}
          placeholderText="Enter the name of your school"
          required={true}
          telemetryLabel="teacher-setup-school-name-input"
          value={schoolName}
          variant="mobile"
        />

        <View style={styles.formContainer}>
          <Text style={styles.formLabel}>
            Your state<Text style={{ color: theme.colors.red }}>*</Text>
          </Text>
          <Select
            value={location}
            onChange={setLocation}
            options={LOCATION_SELECT_OPTIONS}
            placeholder="Select your state"
          />
        </View>

        <View style={[styles.formContainer, styles.teachingStyleContainer]}>
          <Text style={styles.formLabel}>
            Do you teach...<Text style={{ color: theme.colors.red }}>*</Text>
          </Text>
          <RadioButtonGroup
            defaultIndex={teachingStyleOptions.findIndex((option) => option.value === teachingStyle)}
            labelStyle={{ fontSize: theme.fontSizes.small }}
            onSelectOption={(value) => setTeachingStyle(value as 'single' | 'multiple')}
            options={teachingStyleOptions}
          />
        </View>

        {!user?.tosAgreedAt && (
          <Checkbox
            checkboxContainerStyle={styles.checkboxContainer}
            containerStyle={{ marginTop: 'auto' }}
            isChecked={!!tosAgreedAt}
            label={checkboxLabel}
            onToggle={handleAgreedToTermsToggle}
            telemetryLabel="teacher-setup-tos-checkbox"
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    gap: theme.spacing.xlarge,
    flexDirection: 'column',
  },
  teachingStyleContainer: {
    paddingRight: theme.spacing.xxlarge,
    width: '90%',
  },
  formLabel: {
    ...baseStyles.boldText,
    fontSize: theme.fontSizes.small,
  },
  checkboxContainer: {
    marginTop: theme.spacing.xlarge,
    gap: theme.spacing.medium,
  },
  checkboxLabelContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xsmall,
    width: '100%',
  },
  checkboxLabelText: {
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.regular,
  },
});
